import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./AddOrganization.css";
import OrganizationTabs from "./OrganizationTabs";

const locationTypes = [
  {
    value: "REGISTERED",
    label: "Registered Office",
  },
  {
    value: "CORPORATE",
    label: "Corporate Office",
  },
  {
    value: "BILLING",
    label: "Billing Address",
  },
  {
    value: "BRANCH",
    label: "Branch",
  },
  {
    value: "PROJECT",
    label: "Project Office",
  },
  {
    value: "FACTORY",
    label: "Factory",
  },
  {
    value: "YARD",
    label: "Yard",
  },
  {
    value: "PORT",
    label: "Port Site",
  },
];

const emptyForm = {
  location_type: "",
  location_code: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country_code: "",
  timezone: "",
  is_head_office: false,
  is_default_billing: false,
  is_default_project_location: false,
  is_active: true,
};

const AddLocation = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([
    { ...emptyForm },
  ]);

  const [tenantId, setTenantId] = useState(null);

  // ----------------------------------------------------------
  // GET TENANT ID
  // ----------------------------------------------------------

  useEffect(() => {
    const storedTenantUser =
      localStorage.getItem("tenant_user");

    if (!storedTenantUser) {
      Swal.fire({
        icon: "error",
        title: "Tenant information missing",
        text: "Please login again.",
      });

      return;
    }

    try {
      const tenantUser = JSON.parse(
        storedTenantUser
      );

      if (!tenantUser.tenant_id) {
        Swal.fire({
          icon: "error",
          title: "Tenant ID missing",
          text:
            "Tenant information could not be found.",
        });

        return;
      }

      setTenantId(
        Number(tenantUser.tenant_id)
      );
    } catch (error) {
      console.error(
        "Invalid tenant_user:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Invalid tenant session",
        text: "Please login again.",
      });
    }
  }, []);

  // ----------------------------------------------------------
  // FETCH LOCATIONS
  // ----------------------------------------------------------

  useEffect(() => {
    if (tenantId) {
      fetchLocations();
    }
  }, [tenantId]);

const fetchLocations = async () => {
  try {
    setLoading(true);

    const response = await axios.post(
      `${API_BASE}/tenant-records/`,
      {
        tenant_id: Number(tenantId),
        tables: ["tenant_locations"],
      }
    );

    console.log(
      "Tenant locations response:",
      response.data
    );

    let data = response.data;

    if (response.data?.data) {
      data = response.data.data;
    }

    // Handle table-wise response
    if (data?.tenant_locations) {
      data = data.tenant_locations;
    }

    if (!Array.isArray(data)) {
      data = [];
    }

    setLocations(data);
  } catch (error) {
    console.error(
      "Error fetching locations:",
      error
    );

    Swal.fire({
      icon: "error",
      title: "Unable to load locations",
      text:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Something went wrong while loading locations.",
    });
  } finally {
    setLoading(false);
  }
};

  // ----------------------------------------------------------
  // HANDLE CHANGE
  // ----------------------------------------------------------

  const handleChange = (index, e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) =>
      prev.map(
        (location, locationIndex) =>
          locationIndex === index
            ? {
                ...location,
                [name]:
                  type === "checkbox"
                    ? checked
                    : value,
              }
            : location
      )
    );
  };

  // ----------------------------------------------------------
  // ADD LOCATION
  // ----------------------------------------------------------

  const handleAdd = () => {
    setFormData([
      {
        ...emptyForm,
        is_head_office: false,
        is_default_billing: false,
        is_default_project_location: false,
        is_active: true,
      },
    ]);

    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // ADD ANOTHER LOCATION
  // ----------------------------------------------------------

  const handleAddAnother = () => {
    setFormData((prev) => [
      ...prev,
      {
        ...emptyForm,
        is_head_office: false,
        is_default_billing: false,
        is_default_project_location: false,
        is_active: true,
      },
    ]);
  };

  // ----------------------------------------------------------
  // REMOVE LOCATION FORM
  // ----------------------------------------------------------

  const handleRemoveLocationForm = (
    index
  ) => {
    setFormData((prev) =>
      prev.filter(
        (_, locationIndex) =>
          locationIndex !== index
      )
    );
  };

  // ----------------------------------------------------------
  // EDIT LOCATION
  // ----------------------------------------------------------

  const handleEdit = (location) => {
    setFormData([
      {
        location_type:
          location.location_type || "",

        location_code:
          location.location_code || "",

        address_line1:
          location.address_line1 || "",

        address_line2:
          location.address_line2 || "",

        city: location.city || "",

        state: location.state || "",

        postal_code:
          location.postal_code || "",

        country_code:
          location.country_code || "",

        timezone:
          location.timezone || "",

        is_head_office:
          location.is_head_office !==
          undefined
            ? location.is_head_office
            : false,

        is_default_billing:
          location.is_default_billing !==
          undefined
            ? location.is_default_billing
            : false,

        is_default_project_location:
          location.is_default_project_location !==
          undefined
            ? location.is_default_project_location
            : false,

        is_active:
          location.is_active !==
          undefined
            ? location.is_active
            : true,
      },
    ]);

    setEditingId(location.id);
    setIsEditing(true);
    setShowForm(true);
  };

  // ----------------------------------------------------------
  // DELETE LOCATION
  // ----------------------------------------------------------

  const handleDelete = async (
    location
  ) => {
    const result =
      await Swal.fire({
        icon: "warning",
        title: "Delete Location?",
        text: `Are you sure you want to delete "${
          location.location_code ||
          location.city ||
          "this location"
        }"?`,
        showCancelButton: true,
        confirmButtonText:
          "Yes, Delete",
        cancelButtonText:
          "Cancel",
        confirmButtonColor:
          "#dc2626",
      });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE}/tenant-locations/${location.id}/`
      );

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text:
          "Location deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      fetchLocations();
    } catch (error) {
      console.error(
        "Error deleting location:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the location.",
      });
    }
  };

  // ----------------------------------------------------------
  // SUBMIT
  // ----------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text:
          "Unable to identify the current tenant.",
      });

      return;
    }

    // --------------------------------------------------------
    // VALIDATE ALL LOCATION FORMS
    // --------------------------------------------------------

    for (
      let i = 0;
      i < formData.length;
      i++
    ) {
      const location = formData[i];

      if (!location.location_type) {
        Swal.fire({
          icon: "warning",
          title: "Location Type Required",
          text: `Please select Location Type for Location ${
            i + 1
          }.`,
        });

        return;
      }

      if (
        !location.address_line1.trim()
      ) {
        Swal.fire({
          icon: "warning",
          title: "Address Required",
          text: `Please enter Address Line 1 for Location ${
            i + 1
          }.`,
        });

        return;
      }
    }

    try {
      setLoading(true);

      // ------------------------------------------------------
      // EDIT SINGLE LOCATION
      // ------------------------------------------------------

      if (isEditing) {
        const location =
          formData[0];

        const payload = {
          location_type:
            location.location_type,

          location_code:
            location.location_code,

          address_line1:
            location.address_line1,

          address_line2:
            location.address_line2,

          city: location.city,

          state: location.state,

          postal_code:
            location.postal_code,

          country_code:
            location.country_code,

          timezone:
            location.timezone,

          is_head_office:
            location.is_head_office,

          is_default_billing:
            location.is_default_billing,

          is_default_project_location:
            location.is_default_project_location,

          is_active:
            location.is_active,

          tenant: Number(
            tenantId
          ),
        };

        await axios.put(
          `${API_BASE}/tenant-locations/${editingId}/`,
          payload
        );

        await Swal.fire({
          icon: "success",
          title: "Location Updated",
          text:
            "Location updated successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      // ------------------------------------------------------
      // CREATE MULTIPLE LOCATIONS
      // ------------------------------------------------------

      else {
        for (
          const location of formData
        ) {
          const payload = {
            location_type:
              location.location_type,

            location_code:
              location.location_code,

            address_line1:
              location.address_line1,

            address_line2:
              location.address_line2,

            city: location.city,

            state: location.state,

            postal_code:
              location.postal_code,

            country_code:
              location.country_code,

            timezone:
              location.timezone,

            is_head_office:
              location.is_head_office,

            is_default_billing:
              location.is_default_billing,

            is_default_project_location:
              location.is_default_project_location,

            is_active:
              location.is_active,

            tenant: Number(
              tenantId
            ),
          };

          await axios.post(
            `${API_BASE}/tenant-locations/`,
            payload
          );
        }

        await Swal.fire({
          icon: "success",
          title: "Locations Created",
          text: `${
            formData.length
          } location${
            formData.length > 1
              ? "s"
              : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false,
        });
      }

      // ------------------------------------------------------
      // RESET
      // ------------------------------------------------------

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);

      setFormData([
        { ...emptyForm },
      ]);

      await fetchLocations();
    } catch (error) {
      console.error(
        "Error saving locations:",
        error
      );

      const backendError =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data ||
        "Unable to save locations.";

      Swal.fire({
        icon: "error",
        title: isEditing
          ? "Update failed"
          : "Creation failed",
        text:
          typeof backendError ===
          "string"
            ? backendError
            : JSON.stringify(
                backendError
              ),
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // CANCEL
  // ----------------------------------------------------------

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);

    setFormData([
      { ...emptyForm },
    ]);
  };

  // ----------------------------------------------------------
  // LOCATION TYPE LABEL
  // ----------------------------------------------------------

  const getLocationTypeLabel = (
    type
  ) => {
    return (
      locationTypes.find(
        (item) =>
          item.value === type
      )?.label ||
      type ||
      "-"
    );
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="organization-page">
          <OrganizationTabs/>
          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="organization-page-header">
                <div>
                  <h2>Locations</h2>
                </div>

                <button
                  type="button"
                  className="organization-add-btn"
                  onClick={
                    handleAdd
                  }
                >
                  + Add Location
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="organization-list-card">
                {loading ? (
                  <div className="organization-loading">
                    Loading locations...
                  </div>
                ) : locations.length ===
                  0 ? (
                  <div className="organization-empty">
                    <div className="organization-empty-icon">
                      +
                    </div>

                    <h3>
                      No Locations
                      Found
                    </h3>

                    <p>
                      There are no
                      locations
                      available for
                      the current
                      tenant.
                    </p>

                    <button
                      type="button"
                      className="organization-empty-add-btn"
                      onClick={
                        handleAdd
                      }
                    >
                      + Add Location
                    </button>
                  </div>
                ) : (
                  <div className="organization-table-wrapper">
                    <table className="organization-table">
                      <thead>
                        <tr>
                          <th>
                            Location
                          </th>

                          <th>
                            Type
                          </th>

                          <th>
                            Address
                          </th>

                          <th>
                            City
                          </th>

                          <th>
                            State
                          </th>

                          <th>
                            Country
                          </th>

                          <th>
                            Status
                          </th>

                          <th>
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {locations.map(
                          (
                            location
                          ) => (
                            <tr
                              key={
                                location.id
                              }
                            >
                              <td>
                                <div className="organization-name-cell">
                                  <div className="organization-avatar">
                                    {location
                                      .location_code
                                      ?.charAt(
                                        0
                                      )
                                      ?.toUpperCase() ||
                                      "L"}
                                  </div>

                                  <div>
                                    <strong>
                                      {location.location_code ||
                                        "-"}
                                    </strong>

                                    <span>
                                      {location.timezone ||
                                        "No timezone"}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <span className="organization-type-pill">
                                  {getLocationTypeLabel(
                                    location.location_type
                                  )}
                                </span>
                              </td>

                              <td>
                                <div className="organization-location">
                                  <strong>
                                    {location.address_line1 ||
                                      "-"}
                                  </strong>

                                  {location.address_line2 && (
                                    <span>
                                      {
                                        location.address_line2
                                      }
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td>
                                {location.city ||
                                  "-"}
                              </td>

                              <td>
                                {location.state ||
                                  "-"}
                              </td>

                              <td>
                                {location.country_code ||
                                  "-"}
                              </td>

                              <td>
                                <span
                                  className={`organization-status ${
                                    location.is_active
                                      ? "active"
                                      : "inactive"
                                  }`}
                                >
                                  {location.is_active
                                    ? "Active"
                                    : "Inactive"}
                                </span>
                              </td>

                              <td>
                                <div className="organization-actions">
                                  <button
                                    type="button"
                                    className="organization-edit-btn"
                                    onClick={() =>
                                      handleEdit(
                                        location
                                      )
                                    }
                                  >
                                    Edit
                                  </button>

                                  <button
                                    type="button"
                                    className="organization-delete-btn"
                                    onClick={() =>
                                      handleDelete(
                                        location
                                      )
                                    }
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="organization-page-header">
                <div>
                  <h2>
                    {isEditing
                      ? "Edit Location"
                      : "Create Locations"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="organization-back-btn"
                  onClick={
                    handleCancel
                  }
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form
                onSubmit={
                  handleSubmit
                }
              >
                {formData.map(
                  (
                    location,
                    index
                  ) => (
                    <div
                      className="organization-card"
                      key={index}
                      style={{
                        marginBottom:
                          "20px",
                      }}
                    >
                      <div className="organization-form-body">
                        {/* ==================================================
                            FORM TITLE
                        ================================================== */}

                        <div
                          className="organization-section-title"
                          style={{
                            display:
                              "flex",
                            justifyContent:
                              "space-between",
                            alignItems:
                              "center",
                          }}
                        >
                          <span>
                            Location{" "}
                            {index +
                              1}
                          </span>

                          {!isEditing &&
                            formData.length >
                              1 && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveLocationForm(
                                    index
                                  )
                                }
                                style={{
                                  border:
                                    "none",
                                  background:
                                    "transparent",
                                  color:
                                    "#dc2626",
                                  cursor:
                                    "pointer",
                                  fontSize:
                                    "13px",
                                  fontWeight:
                                    "600",
                                }}
                              >
                                Remove
                              </button>
                            )}
                        </div>

                        {/* ==================================================
                            LOCATION INFORMATION
                        ================================================== */}

                        <div className="organization-section-title">
                          Location
                          Information
                        </div>

                        <div className="organization-form-grid">
                          {/* LOCATION TYPE */}

                          <div className="organization-form-group">
                            <label>
                              Location
                              Type{" "}
                              <span>
                                *
                              </span>
                            </label>

                            <select
                              name="location_type"
                              value={
                                location.location_type
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              required
                            >
                              <option value="">
                                Select
                                location
                                type
                              </option>

                              {locationTypes.map(
                                (
                                  type
                                ) => (
                                  <option
                                    key={
                                      type.value
                                    }
                                    value={
                                      type.value
                                    }
                                  >
                                    {
                                      type.label
                                    }
                                  </option>
                                )
                              )}
                            </select>
                          </div>

                          {/* LOCATION CODE */}

                          <div className="organization-form-group">
                            <label>
                              Location
                              Code
                            </label>

                            <input
                              type="text"
                              name="location_code"
                              value={
                                location.location_code
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="e.g. LOC001"
                              maxLength={
                                60
                              }
                            />
                          </div>

                          {/* ADDRESS LINE 1 */}

                          <div className="organization-form-group">
                            <label>
                              Address
                              Line 1{" "}
                              <span>
                                *
                              </span>
                            </label>

                            <input
                              type="text"
                              name="address_line1"
                              value={
                                location.address_line1
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter address line 1"
                              maxLength={
                                250
                              }
                              required
                            />
                          </div>

                          {/* ADDRESS LINE 2 */}

                          <div className="organization-form-group">
                            <label>
                              Address
                              Line 2
                            </label>

                            <input
                              type="text"
                              name="address_line2"
                              value={
                                location.address_line2
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter address line 2"
                              maxLength={
                                250
                              }
                            />
                          </div>

                          {/* CITY */}

                          <div className="organization-form-group">
                            <label>
                              City
                            </label>

                            <input
                              type="text"
                              name="city"
                              value={
                                location.city
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter city"
                              maxLength={
                                120
                              }
                            />
                          </div>

                          {/* STATE */}

                          <div className="organization-form-group">
                            <label>
                              State
                            </label>

                            <input
                              type="text"
                              name="state"
                              value={
                                location.state
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter state"
                              maxLength={
                                120
                              }
                            />
                          </div>

                          {/* POSTAL CODE */}

                          <div className="organization-form-group">
                            <label>
                              Postal
                              Code
                            </label>

                            <input
                              type="text"
                              name="postal_code"
                              value={
                                location.postal_code
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="Enter postal code"
                              maxLength={
                                20
                              }
                            />
                          </div>

                          {/* COUNTRY CODE */}

                          <div className="organization-form-group">
                            <label>
                              Country
                              Code
                            </label>

                            <input
                              type="text"
                              name="country_code"
                              value={
                                location.country_code
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="e.g. IN"
                              maxLength={
                                2
                              }
                            />
                          </div>

                          {/* TIMEZONE */}

                          <div className="organization-form-group">
                            <label>
                              Timezone
                            </label>

                            <input
                              type="text"
                              name="timezone"
                              value={
                                location.timezone
                              }
                              onChange={(
                                e
                              ) =>
                                handleChange(
                                  index,
                                  e
                                )
                              }
                              placeholder="e.g. Asia/Kolkata"
                            />
                          </div>

                          {/* HEAD OFFICE */}

                          <div className="organization-status-field">
                            <label>
                              Head Office
                            </label>

                            <label className="organization-switch">
                              <input
                                type="checkbox"
                                name="is_head_office"
                                checked={
                                  location.is_head_office
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleChange(
                                    index,
                                    e
                                  )
                                }
                              />

                              <span className="organization-slider"></span>
                            </label>

                            <span className="organization-status-text">
                              {location.is_head_office
                                ? "Yes"
                                : "No"}
                            </span>
                          </div>

                          {/* DEFAULT BILLING */}

                          <div className="organization-status-field">
                            <label>
                              Default
                              Billing
                            </label>

                            <label className="organization-switch">
                              <input
                                type="checkbox"
                                name="is_default_billing"
                                checked={
                                  location.is_default_billing
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleChange(
                                    index,
                                    e
                                  )
                                }
                              />

                              <span className="organization-slider"></span>
                            </label>

                            <span className="organization-status-text">
                              {location.is_default_billing
                                ? "Yes"
                                : "No"}
                            </span>
                          </div>

                          {/* DEFAULT PROJECT LOCATION */}

                          <div className="organization-status-field">
                            <label>
                              Default
                              Project
                              Location
                            </label>

                            <label className="organization-switch">
                              <input
                                type="checkbox"
                                name="is_default_project_location"
                                checked={
                                  location.is_default_project_location
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleChange(
                                    index,
                                    e
                                  )
                                }
                              />

                              <span className="organization-slider"></span>
                            </label>

                            <span className="organization-status-text">
                              {location.is_default_project_location
                                ? "Yes"
                                : "No"}
                            </span>
                          </div>

                          {/* LOCATION STATUS */}

                          <div className="organization-status-field">
                            <label>
                              Location
                              Status
                            </label>

                            <label className="organization-switch">
                              <input
                                type="checkbox"
                                name="is_active"
                                checked={
                                  location.is_active
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleChange(
                                    index,
                                    e
                                  )
                                }
                              />

                              <span className="organization-slider"></span>
                            </label>

                            <span className="organization-status-text">
                              {location.is_active
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}

                {/* ==================================================
                    ADD ANOTHER LOCATION
                ================================================== */}

                {!isEditing && (
                  <button
                    type="button"
                    className="organization-add-btn"
                    onClick={
                      handleAddAnother
                    }
                    style={{
                      marginBottom:
                        "20px",
                    }}
                  >
                    + Add Another
                    Location
                  </button>
                )}

                {/* ==================================================
                    FORM ACTIONS
                ================================================== */}

                <div className="organization-form-actions">
                  <button
                    type="button"
                    className="organization-cancel-btn"
                    onClick={
                      handleCancel
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="organization-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Location"
                      : "Save Locations"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddLocation;